import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Loader2 } from "lucide-react";
import remarkTabellen from "../lib/remark-tabellen";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/**
 * J7 van blok J (Frank, 18 oktober 2026): een document bekijken zonder downloaden.
 *
 * PDF toont de app zelf, uit de bewaarde inhoud. Word, Excel en PowerPoint gaan
 * naar `POST /conversations/documenten/lees`, dat ze met NovaMunds eigen lezers
 * tot markdown maakt; die verschijnt hier met dezelfde tabelopmaak als in de chat.
 * Er wordt niets opgeslagen, en het werkt ook op de telefoon, door de tunnel.
 */

export const LEESBAAR = new Set(["PDF", "Word", "Excel", "PowerPoint", "Markdown", "HTML"]);

/**
 * NS-A1 (27 oktober 2026): markdown en HTML als voorbeeld, uit de bewaarde inhoud en zonder
 * netwerk. Besluit NS-A1-1: op de BijlageKaart, in dit venster, zonder route (App.js bevroren).
 */
export const EIGEN_LEZING = new Set(["Markdown", "HTML"]);

/** De bewaarde inhoud (base64) als UTF-8-tekst, of null als hij niet te lezen is. PUUR. */
export function tekstVan(att) {
  try {
    const ruw = atob(att?.data || "");
    const bytes = Uint8Array.from(ruw, (c) => c.charCodeAt(0));
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/**
 * **HTML DRAAIT NOOIT ALS DE APP.** Het voorbeeld staat in een iframe met een LEGE sandbox: geen
 * scripts, geen formulieren, geen eigen herkomst, dus geen toegang tot de opslag of de API van de
 * app, en geen navigatie van het hoofdvenster. Er is geen CSP in deze app; deze attribuut is de
 * enige grens, en een wachter eist dat hij leeg blijft.
 */
export const HTML_SANDBOX = "";

/** Dezelfde tabelopmaak als `MessageBubble`: een tabel schuift in zijn eigen venster. */
const TABEL = {
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-3" data-testid="markdown-tabel">
      <table className="text-sm border-collapse" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th className="border border-[hsl(var(--border))] px-2 py-1 text-left align-top font-medium whitespace-normal break-words max-w-[22rem]" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-[hsl(var(--border))] px-2 py-1 align-top whitespace-normal break-words max-w-[22rem]" {...props} />
  ),
};

export default function DocumentBekijken({ att, soort, pdfAdres, open, onOpenChange }) {
  const { t } = useTaal();
  const [stand, setStand] = useState({ laden: false, markdown: "", fout: "" });

  useEffect(() => {
    if (!open || soort === "PDF") return undefined;
    if (EIGEN_LEZING.has(soort)) {
      const tekst = tekstVan(att);
      setStand(tekst === null ? { laden: false, markdown: "", fout: t("Bekijken lukte niet.") }
        : { laden: false, markdown: tekst, fout: "" });
      return undefined;
    }
    let weg = false;
    setStand({ laden: true, markdown: "", fout: "" });
    axios.post(`${API}/conversations/documenten/lees`, { name: att?.name || "", data: att?.data || "" })
      .then((r) => { if (!weg) setStand({ laden: false, markdown: r.data?.markdown || "", fout: "" }); })
      .catch((e) => {
        if (!weg) setStand({ laden: false, markdown: "", fout: e?.response?.data?.detail || t("Bekijken lukte niet.") });
      });
    return () => { weg = true; };
  }, [open, soort, att, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[calc(100vw-2rem)]" data-testid="bekijk-venster">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{att?.name || t("Bijlage")}</DialogTitle>
        </DialogHeader>
        {soort === "PDF" ? (
          pdfAdres ? (
            <iframe src={pdfAdres} title={att?.name || "PDF"} className="h-[75vh] w-full rounded border"
                    data-testid="bekijk-pdf" />
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="bekijk-fout">{t("inhoud niet bewaard")}</p>
          )
        ) : stand.laden ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="bekijk-laden">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{t("Bezig met lezen")}
          </p>
        ) : stand.fout ? (
          <p className="text-sm text-destructive" role="alert" data-testid="bekijk-fout">{stand.fout}</p>
        ) : soort === "HTML" ? (
          <iframe sandbox={HTML_SANDBOX} srcDoc={stand.markdown} title={att?.name || "HTML"}
                  className="h-[75vh] w-full rounded border bg-white" data-testid="bekijk-html" />
        ) : (
          <div className="prose prose-sm dark:prose-invert max-h-[75vh] max-w-none overflow-y-auto" data-testid="bekijk-tekst">
            <ReactMarkdown remarkPlugins={[remarkTabellen]} components={TABEL}>{stand.markdown}</ReactMarkdown>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
